import { useEffect, useState } from "react";
import { Box, Button, Chip, Collapse, Container, Divider, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForward from "@mui/icons-material/ArrowForward";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import Code from "@mui/icons-material/Code";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import CloudOutlined from "@mui/icons-material/CloudOutlined";
import StorageOutlined from "@mui/icons-material/StorageOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import ViewQuiltOutlined from "@mui/icons-material/ViewQuiltOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import GppGoodOutlined from "@mui/icons-material/GppGoodOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import FormularioContacto from "../contact/components/FormularioContacto";
import {
  CONTACT_FORM_ID,
  MODELS_SECTION_ID,
  scrollToContact,
  scrollToId,
} from "./constants";

const CARD_ICONS = [
  <Code key="software" fontSize="small" />,
  <SmartToyOutlined key="ai" fontSize="small" />,
  <CloudOutlined key="cloud" fontSize="small" />,
  <StorageOutlined key="data" fontSize="small" />,
  <ScienceOutlined key="qa" fontSize="small" />,
];

const MODEL_ICONS = [
  <Code key="specialists" fontSize="small" />,
  <StorageOutlined key="cells" fontSize="small" />,
  <ShieldOutlined key="pods" fontSize="small" />,
  <CloudOutlined key="delivery-centers" fontSize="small" />,
];

const EXPERIENCE_ICONS = [
  <Code key="software" fontSize="small" />,
  <StorageOutlined key="data" fontSize="small" />,
  <ScienceOutlined key="qa" fontSize="small" />,
  <CloudOutlined key="cloud" fontSize="small" />,
  <ApartmentOutlined key="enterprise" fontSize="small" />,
  <ViewQuiltOutlined key="delivery" fontSize="small" />,
  <SettingsOutlined key="ops" fontSize="small" />,
];

const CHIP_ICONS = [
  <VerifiedUserOutlined key="iso" fontSize="small" />,
  <GroupsOutlined key="specialists" fontSize="small" />,
  <WorkspacePremiumOutlined key="profiles" fontSize="small" />,
];

const PROCESS_ICONS = [
  <SearchOutlined key="need" />,
  <LightbulbOutlined key="proposal" />,
  <SettingsOutlined key="configure" />,
  <GroupsOutlined key="integrate" />,
];

const MotionBox = motion.create(Box);

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55, ease: "easeOut" as const },
};

export default function StaffAugmentation() {
  const { t, i18n } = useTranslation();
  const rotating = t("pages.staff.hero.rotating", {
    returnObjects: true,
  }) as string[];
  const chips = t("pages.staff.hero.chips", {
    returnObjects: true,
  }) as string[];
  const cards = t("pages.staff.hero.cards", {
    returnObjects: true,
  }) as Array<{ title: string; tags: string[] }>;
  const models = t("pages.staff.models.items", {
    returnObjects: true,
  }) as Array<{
    title: string;
    description: string;
    ideal: string[];
    footerLabel: string;
    footer: string[];
  }>;

  const experienceCategories = t("pages.staff.experience.categories", {
    returnObjects: true,
  }) as Array<{
    name: string;
    roles: Array<{ title: string; description: string }>;
  }>;
  const securityParagraphs = t("pages.staff.security.paragraphs", {
    returnObjects: true,
  }) as string[];
  const visionInternal = t("pages.staff.vision.internal", {
    returnObjects: true,
  }) as string[];
  const visionSpecialists = t("pages.staff.vision.specialists", {
    returnObjects: true,
  }) as string[];
  const visionShared = t("pages.staff.vision.shared", {
    returnObjects: true,
  }) as string[];
  const processSteps = t("pages.staff.process.steps", {
    returnObjects: true,
  }) as Array<{ title: string; description: string }>;
  const faqItems = t("pages.staff.faq.items", {
    returnObjects: true,
  }) as Array<{ question: string; answer: string }>;

  const [activeIndex, setActiveIndex] = useState(0);
  const [experienceCategory, setExperienceCategory] = useState(0);
  const [expandedRole, setExpandedRole] = useState<number | false>(false);
  const [expandedFaq, setExpandedFaq] = useState<number | false>(0);

  useEffect(() => {
    if (!Array.isArray(rotating) || rotating.length === 0) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % rotating.length);
    }, 2800);
    return () => window.clearInterval(timer);
  }, [i18n.language, rotating.length]);

  return (
    <Box component="main" sx={{ width: "100%", bgcolor: "background.default" }}>
      <Box
        component="section"
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
          bgcolor: "text.primary",
          color: "common.white",
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 10 },
          backgroundImage: `radial-gradient(${alpha(
            theme.palette.secondary.main,
            0.18
          )} 1px, transparent 1px)`,
          backgroundSize: "22px 22px",
        })}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 5, md: 8 }} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  color: "common.white",
                  fontSize: { xs: "2.25rem", sm: "3rem", md: "3.5rem" },
                  lineHeight: 1.1,
                  letterSpacing: "-0.03em",
                  mb: 0.5,
                }}
              >
                {t("pages.staff.hero.headline")}
              </Typography>

              <Typography
                variant="h1"
                key={activeIndex}
                sx={{
                  color: "secondary.main",
                  fontSize: { xs: "2.1rem", sm: "2.75rem", md: "3.25rem" },
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  minHeight: { xs: "5.2rem", md: "4.6rem" },
                  mb: 3,
                  animation: "deliveryRotate 0.45s ease",
                  "@keyframes deliveryRotate": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                {Array.isArray(rotating) ? rotating[activeIndex] : ""}
              </Typography>

              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: "common.white",
                  fontWeight: 600,
                  fontSize: { xs: "1.05rem", md: "1.25rem" },
                  mb: 2,
                }}
              >
                {t("pages.staff.hero.lead")}
              </Typography>

              <Typography
                sx={{
                  color: "grey.300",
                  fontSize: { xs: "0.95rem", md: "1rem" },
                  lineHeight: 1.7,
                  mb: 1.5,
                }}
              >
                {t("pages.staff.hero.body")}
              </Typography>
              <Typography
                sx={{
                  color: "grey.300",
                  fontSize: { xs: "0.95rem", md: "1rem" },
                  lineHeight: 1.7,
                  mb: 4,
                }}
              >
                {t("pages.staff.hero.body2")}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={scrollToContact}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 2.75,
                    py: 1.25,
                    borderRadius: 999,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                      bgcolor: "primary.dark",
                    },
                  }}
                >
                  {t("pages.staff.hero.cta")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToId(MODELS_SECTION_ID)}
                  sx={(theme) => ({
                    px: 2.75,
                    py: 1.25,
                    borderRadius: 999,
                    color: "common.white",
                    borderColor: alpha(theme.palette.common.white, 0.45),
                    "&:hover": {
                      borderColor: "common.white",
                      bgcolor: alpha(theme.palette.common.white, 0.06),
                    },
                  })}
                >
                  {t("pages.staff.hero.cta_models")}
                </Button>
              </Box>

              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  pt: 2.5,
                  borderTop: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                })}
              >
                {Array.isArray(chips) &&
                  chips.map((label, index) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "grey.300",
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                      }}
                    >
                      <Box sx={{ color: "secondary.main", display: "flex" }}>
                        {CHIP_ICONS[index]}
                      </Box>
                      {label}
                    </Box>
                  ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {Array.isArray(cards) &&
                  cards.map((card, index) => (
                    <Box
                      key={card.title}
                      sx={(theme) => ({
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.common.white, 0.04),
                        border: `1px solid ${alpha(
                          theme.palette.common.white,
                          0.12
                        )}`,
                      })}
                    >
                      <Box
                        sx={(theme) => ({
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "secondary.main",
                          border: `1px solid ${alpha(
                            theme.palette.secondary.main,
                            0.45
                          )}`,
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        })}
                      >
                        {CARD_ICONS[index]}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "common.white",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            mb: 0.75,
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {card.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={(theme) => ({
                                height: 22,
                                color: "secondary.main",
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                border: `1px solid ${alpha(
                                  theme.palette.secondary.main,
                                  0.25
                                )}`,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                "& .MuiChip-label": { px: 1 },
                              })}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        id={MODELS_SECTION_ID}
        component="section"
        sx={(theme) => ({
          bgcolor: "background.paper",
          py: { xs: 6, md: 8 },
          scrollMarginTop: "6rem",
          backgroundImage: `radial-gradient(${alpha(
            theme.palette.primary.main,
            0.07
          )} 1px, transparent 1px)`,
          backgroundSize: "22px 22px",
        })}
      >
        <Container maxWidth="lg">
          <MotionBox {...fadeUp}>
          <Box sx={{ textAlign: "center", mb: 4, maxWidth: 720, mx: "auto" }}>
            <Box
              sx={{
                display: "inline-flex",
                px: 1.5,
                py: 0.4,
                mb: 1.5,
                borderRadius: 999,
                bgcolor: "primary.light",
                color: "primary.dark",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {t("pages.staff.models.badge")}
            </Box>
            <Typography
              variant="h3"
              sx={{
                color: "primary.dark",
                fontSize: { xs: "1.35rem", md: "1.75rem" },
                lineHeight: 1.25,
                mb: 1,
              }}
            >
              {t("pages.staff.models.title")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", md: "0.95rem" },
                lineHeight: 1.55,
              }}
            >
              {t("pages.staff.models.subtitle")}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              alignItems: "start",
              columnGap: 2,
              rowGap: 2.5,
            }}
          >
            {Array.isArray(models) &&
              models.map((model, index) => (
                <Box
                  key={model.title}
                  sx={{
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {MODEL_ICONS[index]}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.dark",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {model.title}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                      mb: 1.25,
                    }}
                  >
                    {model.description}
                  </Typography>

                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    sx={{
                      color: "primary.main",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 0.4,
                    }}
                  >
                    {t("pages.staff.models.ideal_label")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "primary.dark",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      lineHeight: 1.4,
                      mb: 1.25,
                    }}
                  >
                    {model.ideal.join(" · ")}
                  </Typography>

                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 0.4,
                    }}
                  >
                    {model.footerLabel}
                  </Typography>
                  <Typography
                    sx={{
                      color: "primary.dark",
                      fontStyle: "italic",
                      fontSize: "0.78rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {model.footer.join(" · ")}
                  </Typography>
                </Box>
              ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 5,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={scrollToContact}
              sx={{
                px: 3.5,
                py: 1,
                borderRadius: 999,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  bgcolor: "primary.dark",
                },
              }}
            >
              {t("pages.staff.models.cta")}
            </Button>
          </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          bgcolor: "background.default",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <MotionBox {...fadeUp}>
          <Box sx={{ textAlign: "center", mb: 4, maxWidth: 820, mx: "auto" }}>
            <Box
              sx={{
                display: "inline-flex",
                px: 1.5,
                py: 0.4,
                mb: 1.5,
                borderRadius: 999,
                bgcolor: "primary.light",
                color: "primary.dark",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {t("pages.staff.experience.badge")}
            </Box>
            <Typography
              variant="h3"
              sx={{
                color: "primary.dark",
                fontSize: { xs: "1.35rem", md: "1.75rem" },
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              {t("pages.staff.experience.title")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", md: "0.95rem" },
                lineHeight: 1.55,
              }}
            >
              {t("pages.staff.experience.subtitle")}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
              alignItems: "start",
              columnGap: 3,
              rowGap: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {Array.isArray(experienceCategories) &&
                experienceCategories.map((category, index) => {
                  const isActive = experienceCategory === index;
                  return (
                    <Box
                      key={category.name}
                      component="button"
                      type="button"
                      onClick={() => {
                        setExperienceCategory(index);
                        setExpandedRole(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        width: "100%",
                        textAlign: "left",
                        border: 0,
                        cursor: "pointer",
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        bgcolor: isActive ? "primary.light" : "transparent",
                        color: isActive ? "primary.dark" : "text.secondary",
                        fontFamily: "inherit",
                        "&:hover": {
                          bgcolor: isActive ? "primary.light" : "background.paper",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: isActive ? "primary.main" : "grey.100",
                          color: isActive ? "common.white" : "text.secondary",
                        }}
                      >
                        {EXPERIENCE_ICONS[index]}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          fontSize: "0.85rem",
                          color: "inherit",
                        }}
                      >
                        {category.name}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>

            <Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {Array.isArray(experienceCategories) &&
                  experienceCategories[experienceCategory]?.roles.map((role, roleIndex) => {
                    const isOpen = expandedRole === roleIndex;
                    return (
                      <Box
                        key={role.title}
                        onClick={() =>
                          setExpandedRole(isOpen ? false : roleIndex)
                        }
                        sx={{
                          border: "1px solid",
                          borderColor: isOpen ? "primary.main" : "divider",
                          borderRadius: 2,
                          px: 2,
                          py: 1.1,
                          cursor: "pointer",
                          bgcolor: "background.default",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "secondary.main",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                color: "primary.dark",
                                fontWeight: 700,
                                fontSize: "0.9rem",
                              }}
                            >
                              {role.title}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              bgcolor: isOpen ? "primary.main" : "grey.100",
                              color: isOpen ? "common.white" : "text.secondary",
                            }}
                          >
                            {isOpen ? (
                              <KeyboardArrowUp fontSize="small" />
                            ) : (
                              <KeyboardArrowDown fontSize="small" />
                            )}
                          </Box>
                        </Box>
                        <Collapse in={isOpen}>
                          <Typography
                            sx={{
                              mt: 1,
                              pl: 2.25,
                              color: "text.secondary",
                              fontSize: "0.82rem",
                              lineHeight: 1.5,
                            }}
                          >
                            {role.description}
                          </Typography>
                        </Collapse>
                      </Box>
                    );
                  })}
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  px: 2,
                  py: 1.1,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                  {t("pages.staff.experience.note")}
                </Typography>
              </Box>
            </Box>
          </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "text.primary",
          color: "common.white",
          py: { xs: 7, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: { xs: -48, md: 24 },
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: { xs: "none", md: "block" },
          }}
        >
          <GppGoodOutlined
            sx={{
              fontSize: { md: 320, lg: 380 },
              color: "common.white",
              opacity: 0.08,
            }}
          />
        </Box>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox {...fadeUp} sx={{ maxWidth: 680 }}>
            <Box
              sx={{
                display: "inline-flex",
                px: 1.5,
                py: 0.4,
                mb: 2,
                borderRadius: 999,
                bgcolor: "primary.main",
                color: "common.white",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {t("pages.staff.security.badge")}
            </Box>
            <Typography
              variant="h3"
              sx={{
                color: "common.white",
                fontSize: { xs: "1.6rem", md: "2.15rem" },
                lineHeight: 1.2,
                mb: 2.5,
              }}
            >
              {t("pages.staff.security.title")}
            </Typography>
            {Array.isArray(securityParagraphs) &&
              securityParagraphs.map((paragraph) => (
                <Typography
                  key={paragraph}
                  sx={{
                    color: "grey.300",
                    fontSize: { xs: "0.9rem", md: "0.98rem" },
                    lineHeight: 1.7,
                    mb: 1.75,
                    maxWidth: 620,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            <Box
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                px: 1.75,
                py: 0.85,
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
                color: "common.white",
                fontSize: "0.8rem",
                fontWeight: 600,
              })}
            >
              <VerifiedUserOutlined sx={{ fontSize: 18, color: "secondary.main" }} />
              {t("pages.staff.security.iso")}
            </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        component="section"
        sx={(theme) => ({
          bgcolor: "text.primary",
          color: "common.white",
          py: { xs: 7, md: 10 },
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        })}
      >
        <Container maxWidth="lg">
          <MotionBox {...fadeUp}>
            <Box sx={{ textAlign: "center", maxWidth: 760, mx: "auto", mb: 5 }}>
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.4,
                  mb: 2,
                  borderRadius: 999,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  color: "common.white",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                })}
              >
                {t("pages.staff.vision.badge")}
              </Box>
              <Typography
                variant="h3"
                sx={{
                  color: "common.white",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  lineHeight: 1.25,
                  mb: 2,
                }}
              >
                {t("pages.staff.vision.title")}
              </Typography>
              <Typography
                sx={{
                  color: "grey.300",
                  fontSize: { xs: "0.9rem", md: "0.98rem" },
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                {t("pages.staff.vision.intro")}
              </Typography>
              <Typography
                sx={{
                  color: "common.white",
                  fontWeight: 700,
                  fontSize: { xs: "1rem", md: "1.15rem" },
                  mb: 1,
                }}
              >
                {t("pages.staff.vision.subtitle")}
              </Typography>
              <Typography
                sx={{
                  color: "grey.300",
                  fontSize: { xs: "0.875rem", md: "0.95rem" },
                  lineHeight: 1.6,
                }}
              >
                {t("pages.staff.vision.body")}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                alignItems: "center",
                gap: { xs: 3, md: 3 },
              }}
            >
              <Box
                sx={(theme) => ({
                  border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
                  borderRadius: 2,
                  p: 2,
                })}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: "primary.main",
                      color: "common.white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ViewQuiltOutlined fontSize="small" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("pages.staff.vision.internal_label")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {Array.isArray(visionInternal) &&
                    visionInternal.map((item) => (
                      <Box
                        key={item}
                        sx={(theme) => ({
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        })}
                      >
                        {item}
                      </Box>
                    ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, px: { md: 1 } }}>
                {Array.isArray(visionShared) &&
                  visionShared.map((item) => (
                    <Box
                      key={item}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                    >
                      <CheckCircle sx={{ fontSize: 20, color: "primary.main" }} />
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
              </Box>

              <Box
                sx={(theme) => ({
                  border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
                  borderRadius: 2,
                  p: 2,
                })}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: "common.white",
                      color: "text.primary",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GroupsOutlined fontSize="small" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("pages.staff.vision.specialists_label")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {Array.isArray(visionSpecialists) &&
                    visionSpecialists.map((item) => (
                      <Box
                        key={item}
                        sx={(theme) => ({
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
                          fontSize: "0.85rem",
                          fontWeight: 600,
                        })}
                      >
                        {item}
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          bgcolor: "background.default",
          py: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <MotionBox {...fadeUp}>
            <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: 6 }}>
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.4,
                  mb: 1.75,
                  borderRadius: 999,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: "primary.dark",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                })}
              >
                {t("pages.staff.process.badge")}
              </Box>
              <Typography
                variant="h3"
                sx={{
                  color: "text.primary",
                  fontSize: { xs: "1.4rem", md: "1.85rem" },
                  lineHeight: 1.25,
                }}
              >
                {t("pages.staff.process.title")}
              </Typography>
            </Box>

            <Box sx={{ position: "relative", mb: 6 }}>
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  top: 36,
                  left: "12.5%",
                  right: "12.5%",
                  height: "1px",
                  bgcolor: "divider",
                }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                  columnGap: { xs: 3, md: 2.5 },
                  rowGap: 4,
                }}
              >
                {Array.isArray(processSteps) &&
                  processSteps.map((step, index) => (
                    <Box key={step.title} sx={{ textAlign: "center", px: { md: 1 } }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          position: "relative",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            border: "1px solid",
                            borderColor: "primary.main",
                            color: "primary.main",
                            bgcolor: "background.default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          {PROCESS_ICONS[index]}
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            top: -4,
                            right: -4,
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: "text.primary",
                            color: "common.white",
                            fontSize: "0.62rem",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                          }}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </Box>
                      </Box>
                      <Typography
                        sx={{
                          color: "text.primary",
                          fontWeight: 700,
                          fontSize: { xs: "0.95rem", md: "1rem" },
                          mb: 1,
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.82rem",
                          lineHeight: 1.6,
                          maxWidth: 240,
                          mx: "auto",
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={scrollToContact}
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: "text.primary",
                  color: "common.white",
                  px: 3.5,
                  py: 1.1,
                  borderRadius: 999,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                    bgcolor: "primary.dark",
                  },
                }}
              >
                {t("pages.staff.process.cta")}
              </Button>
            </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          bgcolor: "background.paper",
          py: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <MotionBox {...fadeUp}>
            <Box sx={{ textAlign: "center", mb: 5 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.4,
                  mb: 1.75,
                  borderRadius: 999,
                  bgcolor: "primary.light",
                  color: "primary.dark",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {t("pages.staff.faq.badge")}
              </Box>
              <Typography
                variant="h3"
                sx={{
                  color: "text.primary",
                  fontSize: { xs: "1.4rem", md: "1.85rem" },
                  lineHeight: 1.25,
                }}
              >
                {t("pages.staff.faq.title")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {Array.isArray(faqItems) &&
                faqItems.map((item, index) => {
                  const isOpen = expandedFaq === index;
                  return (
                    <Box
                      key={item.question}
                      onClick={() => setExpandedFaq(isOpen ? false : index)}
                      sx={(theme) => ({
                        border: "1px solid",
                        borderColor: isOpen ? "primary.main" : "divider",
                        borderRadius: 2,
                        px: 2.25,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor: "background.default",
                        boxShadow: isOpen
                          ? `0 1px 8px ${alpha(theme.palette.primary.main, 0.1)}`
                          : `0 1px 4px ${alpha(theme.palette.text.primary, 0.04)}`,
                      })}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: isOpen ? "primary.main" : "text.primary",
                            fontWeight: 700,
                            fontSize: { xs: "0.9rem", md: "0.98rem" },
                            lineHeight: 1.4,
                          }}
                        >
                          {item.question}
                        </Typography>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            bgcolor: isOpen ? "primary.light" : "grey.100",
                            color: isOpen ? "primary.main" : "text.secondary",
                          }}
                        >
                          {isOpen ? (
                            <KeyboardArrowUp fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )}
                        </Box>
                      </Box>
                      <Collapse in={isOpen}>
                        <Typography
                          sx={{
                            mt: 1.25,
                            pr: 4.5,
                            color: "text.secondary",
                            fontSize: "0.88rem",
                            lineHeight: 1.65,
                          }}
                        >
                          {item.answer}
                        </Typography>
                      </Collapse>
                    </Box>
                  );
                })}
            </Box>
          </MotionBox>
        </Container>
      </Box>

      <Box
        id={CONTACT_FORM_ID}
        component="section"
        sx={{
          bgcolor: "background.paper",
          py: { xs: 6, md: 10 },
          scrollMarginTop: "6rem",
        }}
      >
        <Container maxWidth="md">
          <FormularioContacto variant="delivery" />
        </Container>
      </Box>
    </Box>
  );
}
